import { useState, useEffect } from "react";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid2,
  Grow,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  parseDateToISOString4,
  parseDateToLgComun2,
} from "../../../helpers/dateTransform";
import Icon from "../../Icon/Icon";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";
import { Link } from "@mui/material";
import { EGButton } from "../../MainComponents/Button/EGButton/EGButton";
import { useTranslation } from "react-i18next";
import { formatsAviable, officeFormats } from "./ModalAttachments";
// import DocViewer from "react-doc-viewer";
// import { Document, Page } from 'react-pdf';

export const MultimediaView = ({ multimedia, fileKey }) => {
  const theme = useTheme();
  const [t] = useTranslation("components");
  // Show full file information
  const [selectedFile, setSelectedFile] = useState(null);
  // File categorization
  const getFileCategory = (fileType) => {
    const categories = {
      images: formatsAviable.images,
      documents: formatsAviable.documents,
      audios: formatsAviable.audios,
      videos: formatsAviable.videos,
    };

    for (const [type, extensions] of Object.entries(categories)) {
      if (extensions.includes(fileType)) {
        return type;
      }
    }

    return null;
  };
  // File categorization
  const getIconFileCategory = (fileType) => {
    const iconCategories = {
      gallery: formatsAviable.images,
      outline_document_text: formatsAviable.documents,
      volume_high: formatsAviable.audios,
      outline_video: formatsAviable.videos,
    };

    for (const [iconName, extensions] of Object.entries(iconCategories)) {
      if (extensions.includes(fileType)) {
        return iconName;
      }
    }
    // Default icon category if no match found
    return "outline_document";
  };

  const fileTitle = (fileName) => {
    if (fileName?.length > 18) {
      const firstPt = fileName.substring(0, 15);
      return `${firstPt}...`;
    }
    return fileName;
  };
  // Non-image file view
  const DocumentThumb = ({ file = [] }) => {
    return (
      <Card
        sx={{
          p: 1,
          cursor: "pointer",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexFlow: "column nowrap",
          border:
            selectedFile?.[fileKey] === file?.[fileKey]
              ? `3px solid ${theme.palette.orange.main}`
              : `1px solid ${theme.palette.divider}`,
        }}
        elevation={0}
      >
        <Icon name={getIconFileCategory(file.file_type)} />
        <Typography
          align="center"
          fontSize={{ sm: "0.6rem", md: "0.8rem" }}
          fontWeight={700}
        >
          {fileTitle(file.name)}
        </Typography>
        <Typography align="center" fontSize={{ sm: "0.6rem", md: "0.8rem" }}>
          {file.name.split(".").pop().toUpperCase()}
        </Typography>
      </Card>
    );
  };

  // Downloading file
  const fileDownload = async () => {
    fetch(selectedFile[fileKey])
      .then((response) => {
        if (!response.ok) {
          throw new Error("Download error");
        }
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = selectedFile.name; // File name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("File could not be downloaded directly:", error);
        // Opens the file in a new tab
        window.open(selectedFile[fileKey], "_blank");
      });
  };

  // Calculation of image columns according to screen resolution
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isLgDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Grid2
      container
      sx={{
        width: "100%",
        height: "65vh",
        flexDirection: { sx: "column-reverse", md: "row" },
      }}
    >
      {/* File Gallery */}
      <Grid2
        className="dyTheme2"
        borderRadius={"0 0 0 6px"}
        size={{ xs: 12, md: selectedFile ? 6 : 12 }}
        sx={{
          height: selectedFile ? { xs: "31.5vh", md: "65vh" } : "65vh",
          order: { xs: 2, md: 1 },
        }}
        p={1}
      >
        <ImageList
          cols={
            // Screens below sm
            isSmDown
              ? 3
              : // Screens below lg
              isLgDown
              ? !selectedFile
                ? 5
                : 4
              : // Screens above lg
              isLgUp
              ? !selectedFile
                ? 7
                : 4
              : 4 // General case
          }
          rowHeight={"auto"}
          sx={{
            width: "100%",
            maxHeight: selectedFile ? { xs: "30vh", md: "63vh" } : "63vh",
            overflowX: "hidden",
          }}
        >
          {multimedia.map((file, i) => (
            <ImageListItem
              key={i}
              sx={{
                m: 0.45,
                cursor: "pointer",
                position: "relative",
                aspectRatio: "1/1",
                overflow: "hidden",
                "&:hover .MuiImageListItemBar-root": {
                  opacity: { xs: 0, md: 1 },
                },
              }}
              onClick={() => setSelectedFile(file)}
            >
              {/* Image */}
              {file.thumb ? (
                <img
                  srcSet={`${file.thumb}`}
                  src={`${file.thumb}`}
                  alt={file.name}
                  loading="lazy"
                  className="rounded"
                  style={{
                    border:
                      selectedFile?.[fileKey] === file?.[fileKey]
                        ? `3px solid ${theme.palette.orange.main}`
                        : "",
                  }}
                />
              ) : (
                // File
                <DocumentThumb file={file} />
              )}
              <ImageListItemBar
                sx={{
                  background: alpha(theme.palette.black.main, 0.5),
                  transition: `opacity 0.3s ease`,
                  opacity: 0,
                  "& .MuiImageListItemBar-title": {
                    fontSize: "0.6rem",
                    fontWeight: "bold",
                    textAlign: "center",
                  },
                  "& .MuiImageListItemBar-subtitle": {
                    textAlign: "center",
                    fontSize: "0.5rem",
                  },
                }}
                title={file.name}
                subtitle={parseDateToISOString4(file.created_at)}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Grid2>

      {/* Selected File Info */}
      {selectedFile && (
        <Grow
          in={!!selectedFile}
          timeout={750}
          style={{ transformOrigin: "0 0 0" }}
          {...(selectedFile ? { timeout: 1000 } : {})}
        >
          <Grid2
            size={{ xs: 12, md: 6 }}
            borderLeft={{ md: `2px solid ${theme.palette.divider}` }}
            sx={{
              height: { xs: "30vh", md: "65vh" },
              order: { xs: 1, md: 2 },
              overflowY: "auto",
              p: 1,
            }}
            position="relative"
            className="border-bottom"
          >
            {/* Close selected imagen button */}
            <EGIconButton
              iconName="close_icon"
              sx={{ position: "absolute", right: 10, up: 0 }}
              onClick={() => setSelectedFile(null)}
            />
            {/* Title */}
            <Typography
              variant="h6"
              color="secondary"
              textAlign="center"
              my={0.7}
            >
              {t("ModalAttachments.file_details")}
            </Typography>
            {/* Multimedia rendering */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0.5,
                overflow: "hidden",
              }}
            >
              {selectedFile.thumb ? (
                <CardMedia
                  component="img"
                  image={selectedFile[fileKey]}
                  alt={selectedFile.name}
                  sx={{
                    maxHeight: { xs: "10vh", md: "20vh" },
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <FileViewer 
                  selectedFile={selectedFile} 
                  getIconFileCategory={getIconFileCategory}
                  getFileCategory={getFileCategory}
                  fileKey={fileKey}
                />
              )}
            </Box>
            {/* Description text */}
            <CardContent
              sx={{
                px: 1,
                py: 0.5,
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  marginX: "auto",
                  fontWeight: 600,
                  wordWrap: "break-word",
                  mb: 0.75,
                }}
                textAlign="center"
              >
                {`"${selectedFile.name}"`}
              </Typography>
              <Typography textAlign="right" mb={1}>
                <Link
                  onClick={() => window.open(selectedFile[fileKey], "_blank")}
                  underline="hover"
                  marginRight="auto"
                >
                  {t("ModalAttachments.view_full_size")}
                </Link>
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                flexWrap="wrap"
              >
                <Box>
                  <Typography variant="body2">
                    {parseDateToLgComun2(selectedFile.created_at)}
                  </Typography>
                  {selectedFile.size && (
                    <Typography variant="body2">
                      {`${Math.round(selectedFile.size / 1024)} kb`}
                    </Typography>
                  )}
                </Box>

                <EGButton
                  iconName="outline_document_download"
                  iconSize={15}
                  label={t("ModalAttachments.download")}
                  onClick={fileDownload}
                  sx={{
                    marginLeft: "auto",
                    fontSize: "0.7rem",
                    py: 0.33,
                    px: 0.5,
                  }}
                />
              </Box>

              {selectedFile.info && (
                <Box py={1}>
                  <Typography variant="h6" color="secondary" mb={0.33}>
                    {`${t("ModalAttachments.file_description")}:`}
                  </Typography>
                  <Typography
                    textAlign="justify"
                    variant="body2"
                    sx={{
                      maxHeight: "20vh",
                      overflowY: "auto",
                      px: 0.75,
                      whiteSpace: "pre-line",
                      overflowWrap: "break-word",
                      backgroundClip: "padding-box",
                      appearance: "none",
                    }}
                  >
                    {selectedFile.info}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Grid2>
        </Grow>
      )}
    </Grid2>
  );
};

const FileViewer = ({ selectedFile, fileKey, getFileCategory, getIconFileCategory }) => {
  
  const theme = useTheme();

  const [rerender, setRerender] = useState(false);

  const typeFile = getFileCategory(selectedFile?.file_type);
  const fileUrl = selectedFile?.[fileKey];
  const extentionByName = selectedFile.name.split(".").pop();

  useEffect(() => {
    setRerender(true);
  }, [selectedFile]);

  useEffect(() => {
    if (rerender) {
      setRerender(false);
    }
  }, [rerender]);

  if (rerender) {
    return <></>;
  }

  if (!fileUrl) return <p>No hay archivo disponible</p>;

  if (typeFile === "audios") {
    return (
      <audio controls style={{ width: '100%' }}>
        <source src={fileUrl} />
      </audio>
    );
  }

  if (typeFile === "videos") {
    return (
      <video controls style={{ width: '100%', borderRadius: 8 }}>
        <source src={fileUrl} />
      </video>
    );
  }

  if (selectedFile.file_type === "pdf") {
    return (
      <Box sx={{ overflow: 'hidden', width: '100%' }}>
        <iframe
          src={fileUrl}
          style={{ width: '100%', height: '280px', border: 'none', marginTop: -55 }}
        />
      </Box>
      // <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
      //   <Page pageNumber={1} />
      // </Document>
    );
  }

  if (officeFormats.includes(selectedFile.file_type) || officeFormats.includes(extentionByName)) {
    return (

      // <DocViewer documents={[{ uri: fileUrl }]} />

      // <iframe
      //   src={`https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=true`}
      //   style={{ width: '100%', height: '280px', border: 'none' }}
      //   className="preview-google-docs"
      // />

      <iframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
        style={{ width: '100%', height: '280px', border: 'none' }}
      />
    );
  }

  if (selectedFile.file_type === "txt" || selectedFile.file_type === "csv" || selectedFile.file_type === "plain") {
    return (
      <iframe
        src={fileUrl}
        style={{ width: '100%', height: '280px', border: 'none' }}
      />
    );
  }
  return (
    <>
      <Card
        sx={{
          p: 1,
          width: "50%",
          height: { xs: "10vh", md: "15vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexFlow: "column nowrap",
          border: `1px solid ${theme.palette.divider}`,
        }}
        elevation={0}
      >
        <Icon name={getIconFileCategory(selectedFile.file_type)} />
        <Typography align="center" fontSize={{ xs: "0.6rem", md: "0.8rem" }}>
          {selectedFile.file_type.toUpperCase()}
        </Typography>
      </Card>
    </>
  );
};
